import { Head, router } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { route } from '@/utils/route';

interface Plan {
    id: number;
    name: string;
    slug: string;
    type: string;
    description: string | null;
    price: number;
    discount_percent: number;
    discount_limit: number | null;
    free_shipping: boolean;
    features: string[];
    terms: string | null;
}

interface SubscriptionData {
    id: number;
    mp_status: string;
    is_active: boolean;
    card_last_four: string | null;
    card_brand: string | null;
    next_payment_date: string | null;
    started_at: string | null;
    cancelled_at: string | null;
    plan_name: string;
}

interface Props {
    plan: Plan;
    subscription: SubscriptionData | null;
    mpPublicKey: string;
    isAuthenticated: boolean;
    flash?: { success?: string };
    errors?: Record<string, string>;
}

export default function SubscriptionShow({ plan, subscription, mpPublicKey, isAuthenticated, flash, errors }: Props) {
    const [loading, setLoading] = useState(false);
    const [sdkReady, setSdkReady] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [idTypes, setIdTypes] = useState<{ id: string; name: string }[]>([]);
    const [sdkError, setSdkError] = useState<string | null>(null);
    const mpRef = useRef<any>(null);

    const serverErrors = errors ? Object.values(errors) : [];

    // Load MP SDK and fetch identification types
    useEffect(() => {
        if (subscription || !isAuthenticated) return;
        if (!mpPublicKey) {
            setSdkError('La configuración de pagos no está disponible. Contactá al soporte.');
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://sdk.mercadopago.com/js/v2';
        script.async = true;
        script.onload = async () => {
            try {
                // @ts-ignore
                const mp = new window.MercadoPago(mpPublicKey, { locale: 'es-AR' });
                mpRef.current = mp;
                setSdkReady(true);

                try {
                    const types = await mp.getIdentificationTypes();
                    if (types && types.length > 0) {
                        setIdTypes(types);
                    } else {
                        setIdTypes([{ id: 'DNI', name: 'DNI' }]);
                    }
                } catch (e) {
                    setIdTypes([{ id: 'DNI', name: 'DNI' }]);
                }
            } catch (e) {
                console.error('MercadoPago SDK init error:', e);
                setSdkError('No se pudo inicializar el procesador de pagos. Recargá la página e intentá nuevamente.');
            }
        };
        script.onerror = () => {
            setSdkError('No se pudo cargar el procesador de pagos. Verificá tu conexión e intentá nuevamente.');
        };
        document.body.appendChild(script);
    }, [subscription, mpPublicKey]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!mpRef.current) return;

        setFormError(null);
        setLoading(true);

        const form = e.currentTarget;
        const cardNumber = (form.elements.namedItem('cardNumber') as HTMLInputElement).value.replace(/\s/g, '');
        const expMonth = (form.elements.namedItem('expMonth') as HTMLInputElement).value;
        const expYear = (form.elements.namedItem('expYear') as HTMLInputElement).value;
        const securityCode = (form.elements.namedItem('securityCode') as HTMLInputElement).value;
        const cardholderName = (form.elements.namedItem('cardholderName') as HTMLInputElement).value;
        const idType = (form.elements.namedItem('idType') as HTMLSelectElement).value;
        const idNumber = (form.elements.namedItem('idNumber') as HTMLInputElement).value;

        try {
            const token = await mpRef.current.createCardToken({
                cardNumber,
                cardExpirationMonth: expMonth,
                cardExpirationYear: expYear,
                securityCode,
                cardholderName,
                identificationType: idType,
                identificationNumber: idNumber,
            });

            if (!token?.id) {
                setFormError('No se pudo procesar la tarjeta. Verificá los datos e intentá nuevamente.');
                setLoading(false);
                return;
            }

            router.post(route('api.subscription.subscribe', { slug: plan.slug }), {
                card_token_id: token.id,
            }, {
                preserveScroll: true,
                onFinish: () => setLoading(false),
            });
        } catch (e: any) {
            console.error('createCardToken error:', e);
            const msg = e?.message || e?.cause?.[0]?.description || 'Error al procesar la tarjeta. Verificá los datos e intentá nuevamente.';
            setFormError(msg);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setLoading(true);
        router.post(route('api.subscription.cancel', { slug: plan.slug }), {}, {
            preserveScroll: true,
            onFinish: () => {
                setLoading(false);
                setShowCancelConfirm(false);
            },
        });
    };

    const formatPrice = (amount: number) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(amount);
    };

    const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500';

    return (
        <MainLayout>
            <Head title={`${plan.name} - AbastoVirtual`} />
            <div className="mx-auto max-w-2xl px-4 py-8">
                {plan.type !== 'price_intel' && (
                    <div className="mb-6 overflow-hidden rounded-xl shadow-lg">
                        <img
                            src="/img/abasto-plus-banner.webp"
                            alt={plan.name}
                            className="w-full object-cover"
                            style={{ maxHeight: 220 }}
                        />
                    </div>
                )}

                {plan.type === 'price_intel' && (
                    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900">{plan.name}</h1>
                )}

                {plan.description && (
                    <p className="mb-6 text-center text-gray-600">{plan.description}</p>
                )}

                {plan.terms && (
                    <p className="-mt-4 mb-6 text-sm text-gray-500">
                        Al suscribirte aceptás los{' '}
                        <button type="button" onClick={() => setShowTerms(true)} className="text-green-600 underline hover:text-green-700">
                            términos y condiciones
                        </button>.
                    </p>
                )}

                {flash?.success && (
                    <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                        {flash.success}
                    </div>
                )}

                {serverErrors.length > 0 && (
                    <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                        {serverErrors.map((e, i) => <p key={i}>{e}</p>)}
                    </div>
                )}

                {/* Plan benefits */}
                <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">
                        {plan.type === 'price_intel' ? 'Qué incluye' : 'Beneficios del plan'}
                    </h2>
                    <ul className="space-y-3">
                        {plan.type === 'price_intel' ? (
                            plan.features.map((feature, i) => (
                                <li key={i} className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </span>
                                    <span className="text-gray-700">{feature}</span>
                                </li>
                            ))
                        ) : (
                            <>
                                <li className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600">%</span>
                                    <span className="text-gray-700">
                                        <strong>{plan.discount_percent}% de descuento</strong> en cada compra, todos los días
                                    </span>
                                </li>
                                {plan.free_shipping && (
                                    <li className="flex items-center gap-3">
                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                                        </span>
                                        <span className="text-gray-700"><strong>Envío gratis</strong> en todas tus compras</span>
                                    </li>
                                )}
                                <li className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    </span>
                                    <span className="text-gray-700"><strong>Atención personalizada</strong> y prioritaria</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-purple-600">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                                    </span>
                                    <span className="text-gray-700"><strong>Productos y novedades</strong> exclusivas</span>
                                </li>
                            </>
                        )}
                    </ul>
                    <div className="mt-6 border-t border-gray-100 pt-4">
                        <p className="text-2xl font-bold text-gray-900">{formatPrice(plan.price)}<span className="text-base font-normal text-gray-500">/mes</span></p>
                    </div>
                </div>

                {/* Subscribed state */}
                {subscription && (
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <h2 className="mb-4 text-lg font-semibold text-gray-900">Tu suscripción</h2>
                        {subscription.mp_status === 'cancelled' && subscription.is_active && (
                            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                Tu suscripción fue cancelada, pero tus beneficios continúan hasta el <strong>{subscription.next_payment_date}</strong>.
                            </div>
                        )}

                        <div className="space-y-2 text-sm text-gray-600">
                            <p>
                                <span className="font-medium text-gray-900">Estado:</span>{' '}
                                {subscription.mp_status === 'authorized' && <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Activa</span>}
                                {subscription.mp_status === 'pending' && <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">Pendiente</span>}
                                {subscription.mp_status === 'paused' && <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">Pausada</span>}
                                {subscription.mp_status === 'cancelled' && <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">Cancelada</span>}
                            </p>
                            <p><span className="font-medium text-gray-900">Plan:</span> {subscription.plan_name}</p>
                            {subscription.card_last_four && (
                                <p><span className="font-medium text-gray-900">Tarjeta:</span> {subscription.card_brand?.toUpperCase()} •••• {subscription.card_last_four}</p>
                            )}
                            {subscription.mp_status !== 'cancelled' && subscription.next_payment_date && (
                                <p><span className="font-medium text-gray-900">Próximo cobro:</span> {subscription.next_payment_date}</p>
                            )}
                            {subscription.started_at && (
                                <p><span className="font-medium text-gray-900">Suscripto desde:</span> {subscription.started_at}</p>
                            )}
                            {subscription.cancelled_at && (
                                <p><span className="font-medium text-gray-900">Cancelada el:</span> {subscription.cancelled_at}</p>
                            )}
                        </div>

                        {subscription.mp_status !== 'cancelled' && (
                            <div className="mt-6">
                                {!showCancelConfirm ? (
                                    <button
                                        type="button"
                                        onClick={() => setShowCancelConfirm(true)}
                                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                                    >
                                        Cancelar suscripción
                                    </button>
                                ) : (
                                    <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                                        <p className="mb-3 text-sm text-red-800">¿Estás seguro de que querés cancelar tu suscripción? Tus beneficios continuarán hasta el fin del período pagado.</p>
                                        <div className="flex gap-3">
                                            <button
                                                type="button"
                                                onClick={handleCancel}
                                                disabled={loading}
                                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                            >
                                                {loading ? 'Cancelando...' : 'Sí, cancelar'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowCancelConfirm(false)}
                                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                No, mantener
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Login CTA for unauthenticated users */}
                {!subscription && !isAuthenticated && (
                    <div className="rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
                        <h2 className="mb-2 text-lg font-semibold text-gray-900">Suscribite a {plan.name}</h2>
                        <p className="mb-5 text-sm text-gray-500">
                            {plan.type === 'price_intel'
                                ? 'Iniciá sesión o creá tu cuenta para suscribirte y empezar a recibir los precios.'
                                : 'Iniciá sesión o creá tu cuenta para suscribirte y empezar a ahorrar.'}
                        </p>
                        <a
                            href={`/login?returnTo=${encodeURIComponent(`/p/${plan.slug}`)}`}
                            className="inline-block rounded-lg bg-green-600 px-6 py-3 text-sm font-semibold text-white hover:bg-green-700"
                        >
                            Iniciar sesión
                        </a>
                    </div>
                )}

                {/* Card form for authenticated non-subscribed users */}
                {!subscription && isAuthenticated && (
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <h2 className="mb-1 text-lg font-semibold text-gray-900">Suscribite</h2>
                        <p className="mb-4 text-sm text-gray-500">
                            Usamos <strong>MercadoPago</strong> como pasarela de pago segura. Tus datos de tarjeta nunca pasan por nuestros servidores.
                            Podés cancelar en cualquier momento. Consultá nuestra{' '}
                            <a href={route('policy.privacy')} className="text-green-600 underline hover:text-green-700">política de privacidad</a>.
                        </p>

                        {sdkError && (
                            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                {sdkError}
                            </div>
                        )}

                        {formError && (
                            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                {formError}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Número de tarjeta</label>
                                    <input type="text" name="cardNumber" inputMode="numeric" autoComplete="cc-number" placeholder={import.meta.env.DEV ? '4509 9535 6623 3704' : '1234 5678 9012 3456'} maxLength={23} required className={inputClass} />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Mes</label>
                                        <input type="text" name="expMonth" inputMode="numeric" autoComplete="cc-exp-month" placeholder="11" maxLength={2} required className={inputClass} />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Año</label>
                                        <input type="text" name="expYear" inputMode="numeric" autoComplete="cc-exp-year" placeholder="25" maxLength={2} required className={inputClass} />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">CVV</label>
                                        <input type="text" name="securityCode" inputMode="numeric" autoComplete="cc-csc" placeholder="123" maxLength={4} required className={inputClass} />
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Titular de la tarjeta</label>
                                    <input type="text" name="cardholderName" autoComplete="cc-name" placeholder={import.meta.env.DEV ? 'APRO' : 'Como figura en la tarjeta'} required className={inputClass} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Tipo de documento</label>
                                        <select name="idType" required className={inputClass}>
                                            {idTypes.length > 0 ? idTypes.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            )) : (
                                                <option value="DNI">DNI</option>
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Número de documento</label>
                                        <input type="text" name="idNumber" inputMode="numeric" placeholder="12345678" required className={inputClass} />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !sdkReady}
                                className="mt-6 w-full rounded-lg bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                            >
                                {loading ? 'Procesando...' : `Suscribirme por ${formatPrice(plan.price)}/mes`}
                            </button>

                            <p className="mt-3 text-center text-xs text-gray-500">
                                Se realizará un débito mensual automático. Podés cancelar en cualquier momento.
                            </p>
                        </form>
                    </div>
                )}
            </div>

            {/* Terms modal */}
            {showTerms && plan.terms && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowTerms(false)}>
                    <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Términos y Condiciones</h2>
                            <button type="button" onClick={() => setShowTerms(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="prose prose-sm max-w-none text-gray-700">
                            <ReactMarkdown>{plan.terms}</ReactMarkdown>
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
}
